import {
  alpha,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Logo } from "assets/index";
import EyeHiddenIcon from "assets/svg/EyeHiddenIcon";
import EyeVisibleIcon from "assets/svg/EyeVisibleIcon";
import { AxiosError, AxiosResponse } from "axios";
import { commonStyle } from "pages/Dashboard/common.styles";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ADMIN_LOGIN } from "reduxStore/actions/authActionType";
import { AdminLoginResponse, AuthLogin } from "reduxStore/dataTypes";
import { authSuccess } from "reduxStore/reducers/authReducer";
import { RootState } from "reduxStore/store";
import { adminLogin } from "services/apiService";
import { saveToStore, StoreType } from "services/localStorage";
import theme, { responsiveFontSize } from "themes/Theme";
import { object } from "zod";

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, error, loading } = useSelector(
    (state: RootState) => state.adminAuth
  );
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handleTogglePassword = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const validate = () => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email === null || email === undefined || email === "") {
      setEmailError("Email is required");
      return false;
    } else if (!regex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    } else if (password === null || password === undefined || password === "") {
      setPasswordError("Password is required");
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (user) {
      if (
        user?.data?.role &&
        (user?.data?.role?.toLowerCase() === "admin" ||
          user?.data?.role?.toLowerCase() === "super admin")
      ) {
        toast.success("Login successful");
        saveToStore(user.data, StoreType.AUTH);
        saveToStore(user?.data?.token, StoreType.AUTH_TOKEN);
        navigate("/admin/dashboard");
      } else {
        toast.error("You are not authorized to login");
      }
    } else if (error) {
      toast.error(error);
    }
  }, [user, error]);

  const handleLogin = async () => {
    if (validate()) {
      const body: AuthLogin = {
        email: email || "",
        password: password || "",
      };
      dispatch({ type: ADMIN_LOGIN, payload: body });
    }
  };

  const handleKeyDown = (key: string) => {
    if (key === "Enter") {
      handleLogin();
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.cardContainer}>
        <center>
          <Box
            component={"img"}
            sx={styles.logoStyle}
            src={Logo}
            onClick={() => navigate("/")}
          />
          <Typography sx={styles.formTitle}>Admin Login</Typography>
        </center>

        <Typography sx={styles.formLabel}>Email Address</Typography>
        <TextField
          variant="outlined"
          fullWidth
          tabIndex={0}
          placeholder="Enter address here"
          slotProps={commonStyle.inputSlotTextStyle}
          onChange={(e) => {
            if (e.target.value) {
              setEmailError(null);
            }
            setEmail(e.target.value);
          }}
          value={email}
          error={!!emailError}
          helperText={emailError || " "}
        />

        <Typography sx={styles.formLabel}>Password</Typography>
        <TextField
          variant="outlined"
          fullWidth
          tabIndex={1}
          type={isPasswordVisible ? "text" : "password"}
          placeholder="Enter password"
          onChange={(e) => {
            if (e.target.value) {
              setPasswordError(null);
            }
            setPassword(e.target.value);
          }}
          onKeyDown={(e) => handleKeyDown(e.key)}
          value={password}
          error={!!passwordError}
          helperText={passwordError || " "}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePassword}
                    aria-label="toggle password visibility"
                  >
                    {isPasswordVisible ? <EyeHiddenIcon /> : <EyeVisibleIcon />}
                  </IconButton>
                </InputAdornment>
              ),
              sx: styles.inputSlotTextStyle,
            },
          }}
        />

        <Stack direction={"row"} alignItems={"center"} gap={1} mt={4}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Button
              variant="contained"
              size="large"
              sx={{ borderRadius: "0.5em" }}
              onClick={handleLogin}
            >
              <Typography sx={styles.buttonStyle}>Login</Typography>
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
export default LoginPage;
const styles = {
  container: {
    background: `linear-gradient(150deg, ${alpha(
      theme.palette.primary.dark,
      1
    )} 0%, ${alpha(theme.palette.primary.main, 0.8)} 50%,${alpha(
      theme.palette.primary.light,
      0.8
    )} 100%)`,
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardContainer: {
    padding: "1em 2em",
    borderRadius: "0.8em",
    backgroundColor: theme.palette.background.default,
    boxShadow: `rgba(17, 17, 26, 0.1) 0px 0px 16px`,
    minWidth: "40%",
  },
  formTitle: {
    fontSize: responsiveFontSize(20),
    fontWeight: 400,
    color: theme.palette.primary.main,
    marginBottom: "0.5em",
  },
  formLabel: {
    fontSize: responsiveFontSize(12),
    fontWeight: 400,
    color: theme.palette.primary.main,
    marginTop: 1,
    marginBottom: 0.5,
  },
  inputSlotTextStyle: {
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    fontWeight: 400,
  },
  buttonStyle: {
    fontSize: responsiveFontSize(16),
    fontWeight: 400,
    color: "#fff",
    textTransform: "capitalize",
  },
  logoStyle: {
    width: 120,
    height: 120,
    objectFit: "contain",
    "&:hover": {
      cursor: "pointer",
    },
  },
};
